import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import GalleryViewer from "../components/GalleryViewer";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function Energiramme() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "Hvad er energiramme?",
      a: "Energirammen er en fastsat grænse for, hvor meget energi en bygning må bruge pr. kvadratmeter pr. år. Den dækker energiforbruget til opvarmning, køling, ventilation og belysning. Formålet er at sikre, at bygninger er energieffektive og lever op til de standarder, der er fastsat i det danske bygningsreglement.",
    },
    {
      q: "Hvad er en energirammeberegning?",
      a: "En energirammeberegning er en teknisk vurdering af, om et byggeri overholder de krav, der stilles til energiforbruget. Beregningen udføres af en certificeret energikonsulent på baggrund af bygningens udformning, isolering, materialevalg og tekniske installationer. Resultatet viser, om projektet holder sig inden for den tilladte energiramme.",
    },
    {
      q: "Hvornår er der krav om energirammeberegning?",
      a: "Der er krav om energirammeberegning ved alt nybyggeri og ved større ombygninger eller tilbygninger, hvor energiforbruget ændres væsentligt. Kravet er en del af bygningsreglementet, som fastsætter grænser for bygningers samlede energiforbrug. Beregningen skal som regel fremlægges for kommunen i forbindelse med byggetilladelsen.",
    },
    {
      q: "Hvad koster en energirammeberegning?",
      a: "Prisen på en energirammeberegning afhænger af bygningens størrelse, kompleksitet og formål. Enkle bygninger kræver typisk mindre arbejde, mens større eller mere teknisk avancerede projekter kræver mere omfattende beregninger. Det anbefales at indhente et tilbud fra en certificeret energikonsulent for at få en præcis pris.",
    },
    {
      q: "Hvordan beregner man energirammen?",
      a: "Beregningen udføres i tre hovedtrin:\n1. Dataindsamling: Oplysninger om bygningens design, materialer og installationer registreres.\n2. Energimodellering: Energiforbruget simuleres i et beregningsprogram, der tager højde for faktorer som klima, isolering og teknik.\n3. Optimering: Hvis bygningen overskrider grænsen, justeres designet, fx ved at forbedre isolering eller installere energieffektive løsninger. Resultatet er en dokumenteret energiramme, der viser bygningens samlede energibehov i kWh/m² pr. år.",
    },
    {
      q: "Hvordan hænger energiramme og bygningsreglementet sammen?",
      a: "Energirammen er en central del af bygningsreglementets energikrav. Reglementet fastlægger de maksimale energiforbrug, som nye bygninger må have, og energirammeberegningen bruges til at dokumentere, at kravene er opfyldt. Uden en godkendt beregning kan der ikke opnås byggetilladelse eller ibrugtagningstilladelse.",
    },
    {
      q: "Er energiramme et krav ved tilbygninger?",
      a: "Ja. Ved tilbygninger og større ombygninger gælder kravet om energiramme, hvis projektet medfører ændringer i bygningens samlede energiforbrug. I praksis betyder det, at nye bygningsdele skal leve op til de samme energikrav som ved nybyggeri, så bygningens samlede energistandard ikke forringes.",
    },
    {
      q: "Hvor lang tid tager det at lave en energirammeberegning?",
      a: "Tidsforbruget afhænger af projektets størrelse og hvor hurtigt de nødvendige oplysninger kan fremskaffes. For et typisk enfamiliehus tager processen som regel få arbejdsdage, mens større eller mere komplekse bygninger kan kræve længere tid.",
    },
  ];

  const primaryCities = [
    "Horsens",
    "Vejle",
    "Aarhus",
    "Skanderborg",
    "Juelsminde",
    "Aalborg",
    "Herning",
    "Randers",
    "Fredericia",
    "Kolding",
    "Odense",
    "Svendborg",
  ];

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="Beregning af energiramme i hele Jylland og Fyn | Få et gratis tilbud"
        description="Vi tilbyder energirammeberegning og vurdering for byggeprojekter og eksisterende bygninger over hele Jylland og Fyn. Kontakt os for et uforpligtende tilbud."
        canonical="https://etdanmark.com/energiramme"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07445_ff41977b.jpg"
      />
      {/* 1. TOP SLIDER HERO */}
      <PageTopSlider
        title="Energiramme"
        subtitle="Overvejer du at starte et byggeprojekt eller renovere din eksisterende ejendom? Vi beregner din energiramme professionelt, så du hurtigt og sikkert opnår byggetilladelse."
        badge="Lovpligtig beregning til byggetilladelse"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Ydelser", to: "/#ydelser" },
          { label: "Energiramme" },
        ]}
        actions={
          <>
            <a
              href="#kontakt"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
              <span>Få et gratis tilbud</span>
            </a>
            <Link
              to="/prisberegner"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-calculator text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Prisberegner</span>
            </Link>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07384.jpg",
          alt: "Energiramme beregning plantegninger og arkitekttegninger",
        }}
      />

      {/* RÅDGIVNING & INTRODUKTION */}
      <section className="py-14 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvad er en energiramme?
              </h2>

              <div className="space-y-3 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Energiramme er en nøgleparameter, der definerer den maksimale mængde tilført energi, dit byggeprojekt må forbruge pr. kvadratmeter pr. år.
                </p>
                <p>
                  Korrekt energirammeberegning sikrer ikke kun overholdelse af lovgivningen, men bidrager også til at minimere energiforbruget og dermed dine langsigtede driftsomkostninger.
                </p>
              </div>

              <div className="pt-3 flex flex-wrap gap-3.5">
                <a
                  href="#kontakt"
                  className="px-6 py-3.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-bold text-sm shadow transition-all flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                  <span>Kontakt os</span>
                </a>
                <Link
                  to="/prisberegner"
                  className="px-6 py-3.5 rounded-full bg-white hover:bg-stone-50 border border-stone-300 text-stone-800 font-bold text-sm shadow-2xs transition-all flex items-center gap-2"
                >
                  <i className="fa-solid fa-calculator text-emerald-700" aria-hidden="true"></i>
                  <span>Gå til Prisberegner</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-stone-100 bg-stone-100 aspect-16/11">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07465-2_62dacd70.jpg"
                  alt="Energiramme bygningskonstruktion og detaljer"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. HVAD MENES DER MED ENERGIRAMME */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvad menes der med energiramme?
              </h2>
              <div className="space-y-3 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  En energiramme sætter en øvre grænse for, hvor meget energi en bygning må forbruge til opvarmning, køling, ventilation og belysning gennem et helt år. Denne ramme beregnes typisk i kilowatt-timer pr. kvadratmeter (kWh/m²).
                </p>
                <p>
                  For at opnå den mest præcise energirammeberegning, skal faktorer som bygningens isolering, geografiske placering og anvendte materialer tages i betragtning.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6 bg-emerald-900 text-white p-8 rounded-3xl space-y-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-800 text-emerald-300 flex items-center justify-center text-lg">
                  <i className="fa-solid fa-gauge-high" aria-hidden="true"></i>
                </div>
                <h3 className="text-lg font-bold">Måleenhed: kWh/m² pr. år</h3>
              </div>
              <p className="text-xs sm:text-sm text-emerald-100 leading-relaxed">
                Energirammen samler hele bygningens energiforbrug i ét samlet tal. Ved at optimere konstruktion og materialevalg tidligt i processen undgår du dyre tilretninger undervejs.
              </p>
              <div className="pt-2 border-t border-emerald-800/80 grid grid-cols-2 gap-3 text-xs text-emerald-200">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-400" aria-hidden="true"></i>
                  <span>Opvarmning & ventilation</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-400" aria-hidden="true"></i>
                  <span>Isolering & tæthed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. PROCESSEN (TRIN FOR TRIN) */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Processen for energirammeberegning
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-3 leading-relaxed">
              Beregningen af energirammen kan virke kompleks, men den er afgørende for at sikre, at dit byggeprojekt overholder de gældende energikrav. Her er nogle trin, der typisk indgår i processen:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Trin 1 */}
            <div className="bg-white p-7 rounded-2xl border border-stone-200 shadow-2xs relative">
              <div className="w-10 h-10 rounded-xl bg-[#165B4C] text-white font-bold flex items-center justify-center text-sm mb-4">
                1
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">Indsamling af data</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Det første skridt er at indsamle detaljerede oplysninger om bygningens design og de materialer, der vil blive brugt.
              </p>
            </div>

            {/* Trin 2 */}
            <div className="bg-white p-7 rounded-2xl border border-stone-200 shadow-2xs relative">
              <div className="w-10 h-10 rounded-xl bg-[#165B4C] text-white font-bold flex items-center justify-center text-sm mb-4">
                2
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">Modellering af bygningens energibehov</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Ved hjælp af avancerede softwareprogrammer modelleres bygningens forventede energiforbrug baseret på de indsamlede data.
              </p>
            </div>

            {/* Trin 3 */}
            <div className="bg-white p-7 rounded-2xl border border-stone-200 shadow-2xs relative">
              <div className="w-10 h-10 rounded-xl bg-[#165B4C] text-white font-bold flex items-center justify-center text-sm mb-4">
                3
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">Optimering af designet</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Hvis den oprindelige model overskrider den tilladte energiramme, skal designet justeres, fx ved at forbedre isoleringen eller installere mere energieffektive systemer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. TO-KOLONNE: OVERHOLDELSE AF LOVGIVNINGEN & LANGSIGTEDE FORDELE */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 rounded-3xl bg-stone-50 border border-stone-200/80 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg mb-2">
                <i className="fa-solid fa-scale-balanced" aria-hidden="true"></i>
              </div>
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Overholdelse af lovgivningen
              </h2>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                I Danmark er overholdelse af energirammen ikke blot et spørgsmål om bæredygtighed, men også en juridisk forpligtelse. Lovgivningen kræver, at alle nye bygninger opfylder specifikke energistandarder, som er defineret i bygningsreglementet. Ved at sikre, at dit byggeprojekt overholder disse standarder, undgår du potentielle bøder og forsinkelser.
              </p>
            </div>

            <div className="p-8 rounded-3xl bg-stone-50 border border-stone-200/80 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-lg mb-2">
                <i className="fa-solid fa-piggy-bank" aria-hidden="true"></i>
              </div>
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Langsigtede fordele
              </h2>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Ved at investere i en præcis energirammeberegning, sikrer du ikke kun overholdelse af lovgivningen, men også betydelige økonomiske besparelser i det lange løb. En energieffektiv bygning har lavere driftsomkostninger, da den kræver mindre energi til opvarmning, køling og belysning. Derudover bidrager det til en øget ejendomsværdi og en bedre samvittighed i forhold til miljøpåvirkningen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. ENERGIRAMME VURDERINGER I HELE JYLLAND OG FYN */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">
              Energiramme vurderinger i hele Jylland og Fyn
            </h2>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Vi tilbyder energirammeberegning og vurdering for byggeprojekter og eksisterende bygninger over hele Jylland og Fyn. Som certificerede energikonsulenter er vi klar til at assistere dig i at sikre, at dine bygninger ikke blot overholder de gældende energikrav, men også optimeres for at opnå maksimal energieffektivitet.
            </p>
            <p className="text-sm font-semibold text-stone-800">
              Her er nogle af de primære byer, hvor vi tilbyder vores ydelser:
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {primaryCities.map((city) => (
                <span
                  key={city}
                  className="px-3.5 py-1.5 rounded-lg bg-white border border-stone-200 text-xs font-semibold text-stone-800 shadow-2xs"
                >
                  {city}
                </span>
              ))}
            </div>
            <p className="text-stone-600 text-sm leading-relaxed pt-2">
              Ud over disse byer dækker vi også bredere områder som Esbjerg, Silkeborg, Viborg, og mange flere. Vi stræber efter at tilbyde vores ekspertise i energirammeberegninger i hele Jylland og Fyn for at sikre, at alle kan opnå de nødvendige certificeringer og forbedringer, der kan øge ejendomsværdien og reducere energiforbruget.
            </p>
          </div>
        </div>
      </section>

      {/* 6. CMS GALLERI INTEGRATION FOR ENERGIRAMME */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <GalleryViewer
            gallerySlug="energiramme"
            title="Galleri: Energiramme projekter"
            subtitle="Eksempler på byggerier og rådgivningsprojekter udført i Jylland og Fyn"
          />
        </div>
      </section>

      {/* 7. OFTE STILLEDE SPØRGSMÅL OM ENERGIRAMME */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Ofte stillede spørgsmål om energiramme
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Få svar på de mest almindelige spørgsmål om energirammeberegning og bygningsreglementet.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-stone-200/90 overflow-hidden shadow-2xs"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 text-left font-bold text-stone-900 flex items-center justify-between gap-4 hover:bg-stone-50/60 transition-colors"
                >
                  <span className="text-sm sm:text-base">{item.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs text-stone-400 transition-transform duration-200 shrink-0 ${
                      openFaq === idx ? "rotate-180 text-[#165B4C]" : ""
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 pt-1 text-xs sm:text-sm text-stone-600 leading-relaxed border-t border-stone-100 whitespace-pre-line">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. SOCIAL PROOF SLIDER */}
      <ReviewsSlider />

      {/* 9. KONTAKT FORMULAR & CALLOUT */}
      <section id="kontakt" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 space-y-5">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Kontakt os for professionel rådgivning
              </h2>

              <p className="text-stone-600 text-sm leading-relaxed">
                Er du klar til at sikre, at dit byggeprojekt opfylder alle krav til energiramme? Hos ET Danmark ApS tilbyder vi detaljeret rådgivning og præcise beregninger, der sikrer at dit projekt ikke blot overholder de gældende krav, men også optimeres til at være så energieffektivt som muligt.
              </p>

              <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200 space-y-3">
                <p className="text-xs text-stone-500 uppercase tracking-wider font-semibold">
                  Kontaktmuligheder
                </p>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-[#165B4C]" aria-hidden="true"></i>
                  <a
                    href="tel:+4542499595"
                    className="font-bold text-stone-900 hover:text-emerald-700 whitespace-nowrap"
                  >
                    42 49 95 95
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-[#165B4C]" aria-hidden="true"></i>
                  <a
                    href="mailto:info@etdanmark.com"
                    className="text-stone-700 hover:text-emerald-700 whitespace-nowrap text-sm"
                  >
                    info@etdanmark.com
                  </a>
                </div>
              </div>

              <p className="text-xs text-stone-500 leading-relaxed">
                For mere information eller for at starte et samarbejde, kontakt os gerne på telefon 42 49 95 95 eller via e-mail info@etdanmark.com.
              </p>
            </div>

            <div className="lg:col-span-7">
              <ContactForm
                defaultService="Energiramme"
                title="Kontakt os"
                subtitle="Du kan let og hurtigt sende os en besked i nedenstående formular."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
